import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip } from '@mui/material';
import {
  gatesByProximity, formatLevel, formatDistance, entryRuleRows, entryVerdict,
} from '../utils/replayDiagnostics';
import { glassSurface } from '../utils/glassSurface';
import { useHighTransparency } from '../utils/glassTransparency';

/**
 * THE FSM'S REASONING ON ONE BAR, which is the step-through debugger's whole screen.
 *
 * <h2>Two panels, because two different questions get asked</h2>
 *
 * While flat the question is "why was this setup skipped", answered by every entry rule's verdict
 * with its numbers. While a premise is open it is "which gate is about to end this", answered by
 * every gate's level ordered by distance to price. Both are shown whenever both exist — a bar can
 * carry an open premise AND a passing entry checklist, and that combination is the one most often
 * misread as a broken engine.
 *
 * <h2>Nearest gate first, and the sign is the reading</h2>
 *
 * A fixed gate order would make a reader compute proximity themselves on every bar. A signed
 * distance says which side of the level price sits on: positive is room remaining, negative means
 * price has already traded through it and the stop is about to take the trade.
 *
 * <h2>Presentational only</h2>
 *
 * Every derivation is in `replayDiagnostics.js` and tested there. jsdom cannot render the chart
 * this sits over, so a component test here would mock the chart away and assert the mock; the
 * interesting properties are all about content.
 */

/**
 * LEGIBILITY IS THE POINT OF THIS PANEL, so the scale lives here rather than inline.
 *
 * <p>The first version used 10–11px at 65% opacity, which is unreadable while actually reading a
 * chart — these numbers are the product, not chrome around it. Everything is 12.5–15px now, dim
 * text is 80% rather than 65%, and the background is near-opaque so text is not competing with
 * candles behind it.
 */
/*
 * READ PER RENDER, not once at module load: the fill follows the reader's transparency override
 * and this panel is docked directly beneath the ledger. One of the two left at a fixed fill is
 * the divergence `glassSurface.js` exists to prevent, arriving by a new route.
 */
const useDenseGlass = () => glassSurface({ dense: true, transparent: useHighTransparency() });

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };

/** Section headings — tracked and uppercase, so they read as structure rather than as data. */
/**
 * The three PTA/PTB management phases, named for a reader rather than by enum.
 *
 * <p>Each names the rule that is IN FORCE, not the state of the market — "PHASE 2 trendline" is
 * what the reader has to check against the chart, and "TREND" alone would not say which exit may
 * fire. Exported so a test can assert every phase the backend can emit is covered.
 */
export const PHASE_LABELS = {
  RISK: 'PHASE 1 · tight stop',
  TREND: 'PHASE 2 · trendline',
  PARABOLIC: 'PHASE 3 · trail',
};

/** Tints that READ as tightening, so the phase is legible without parsing the words. */
export const PHASE_TINTS = {
  RISK: 'rgba(255,107,107,0.22)',
  TREND: 'rgba(107,180,255,0.22)',
  PARABOLIC: 'rgba(255,184,107,0.26)',
};

const SECTION = {
  fontSize: 12, letterSpacing: 0.9, fontWeight: 700,
  color: 'rgba(242,246,251,0.72)', textTransform: 'uppercase',
};

/** The data scale, in one place so a column cannot drift out of step with its heading. */
const DATA = { fontSize: 14, lineHeight: 1.5 };
const DATA_SMALL = { fontSize: 12.5, lineHeight: 1.45 };

/** Column widths, shared by the header row and the data rows so they actually line up. */
const COL_GATE = 112;
const COL_LEVEL = 86;
const COL_DIST = 78;

/** One gate row. */
function GateRow({ reading }) {
  const breached = reading.distance !== null && reading.distance !== undefined
    && reading.distance < 0;
  return (
    <Box
      data-testid="debug-gate-row"
      sx={{
        display: 'flex', alignItems: 'baseline', gap: 1,
        // Real padding and a separator. Four dense rows at 2px of leading is a block of text, not
        // a table, and the whole use of it is reading one row per bar.
        py: 0.65, px: 0.75, borderRadius: 0.75,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        // The binding gate is the one the ratchet took; a breached one is about to end the trade.
        // Colour AND a tint carry that, because a reader scanning three rows every bar will not
        // read text.
        color: breached ? '#ff8a8a' : reading.binding ? '#8ff09b' : 'rgba(242,246,251,0.92)',
        background: reading.binding ? 'rgba(126,231,135,0.10)'
          : breached ? 'rgba(255,110,110,0.10)' : 'transparent',
      }}
    >
      <Typography sx={{ ...MONO, ...DATA, width: COL_GATE, flexShrink: 0, fontWeight: 600 }}>
        {reading.gate}
      </Typography>
      <Typography sx={{ ...MONO, ...DATA, width: COL_LEVEL, textAlign: 'right' }}>
        {formatLevel(reading.level)}
      </Typography>
      <Typography sx={{ ...MONO, ...DATA, width: COL_DIST, textAlign: 'right', opacity: 0.9 }}>
        {formatDistance(reading.distance)}
      </Typography>
      <Typography sx={{ fontSize: 11.5, opacity: 0.85, fontWeight: 600 }}>
        {reading.binding ? 'BINDING' : reading.improves ? 'would tighten' : ''}
      </Typography>
    </Box>
  );
}

GateRow.propTypes = { reading: PropTypes.object.isRequired };

/** One entry rule row. */
function RuleRow({ row }) {
  return (
    <Box
      data-testid="debug-rule-row"
      sx={{
        display: 'flex', gap: 1.25, py: 0.7, px: 0.75, borderRadius: 0.75,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        // The blocker IS the answer to "why was this skipped", so it is tinted rather than left
        // to be found by reading four similar rows.
        background: row.isBlocker ? 'rgba(255,110,110,0.10)' : 'transparent',
        // A rule this run does not apply is dimmed rather than hidden. Hidden, a reader would
        // wonder where the breakout rule went; shown in PASS/FAIL green and red, they would read
        // it as the reason a trade was skipped — which is exactly what happened, on bars the
        // engine had taken.
        opacity: row.consulted ? 1 : 0.42,
      }}
    >
      <Typography
        sx={{
          ...MONO, fontSize: 12, fontWeight: 700, width: 42, flexShrink: 0, pt: 0.15,
          color: !row.consulted ? 'rgba(255,255,255,0.55)'
            : row.passed ? '#8ff09b' : '#ff8a8a',
        }}
      >
        {!row.consulted ? 'n/a' : row.passed ? 'PASS' : 'FAIL'}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ ...DATA, fontWeight: row.isBlocker ? 700 : 500 }}>
          {row.label}
          {row.shortfall && (
            <Typography
              component="span"
              sx={{ fontSize: 12.5, fontWeight: 700, color: '#ffc078', ml: 0.75 }}
            >
              ({row.shortfall})
            </Typography>
          )}
        </Typography>
        <Typography sx={{ ...MONO, ...DATA_SMALL, opacity: 0.82, wordBreak: 'break-word' }}>
          {row.detail}
        </Typography>
      </Box>
    </Box>
  );
}

RuleRow.propTypes = { row: PropTypes.object.isRequired };

export default function DynaManDebugPanel({ frame, stopUpdates, atBreakpoint }) {
  const glass = useDenseGlass();
  const diagnostics = frame && frame.diagnostics;
  if (!diagnostics) return null;

  const gates = gatesByProximity(diagnostics.gates);
  const rules = entryRuleRows(diagnostics.entry);
  const hasOpenTrade = !!frame.trade;

  return (
    <Box
      data-testid="replay-debug-panel"
      sx={{
        ...glass,
        // A BLOCK, not an absolutely-positioned panel: it sits inside the docked column beside
        // the running ledger so all of it is one control centre. Width and scrolling belong to
        // the dock now — two scroll containers, one inside the other, is a panel that moves
        // under the cursor while you are reading it.
        p: 1.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Typography sx={SECTION}>Engine state</Typography>
        <Chip
          label={frame.state}
          size="small"
          sx={{
            height: 24, fontSize: 12, fontWeight: 700,
            bgcolor: 'rgba(255,255,255,0.12)', color: '#f2f6fb',
          }}
        />
        {/* THE MANAGEMENT PHASE, beside the state rather than buried in a row.
            The three phases enforce different exit rules, so "why did nothing exit here" is
            usually answered by the phase -- and inferring it from which gates went quiet is
            exactly the detective work this panel exists to remove. Absent for a run that is not
            phase-managed, which is every CTS and TrendPulse run. */}
        {diagnostics && diagnostics.phase && (
          <Chip
            label={PHASE_LABELS[diagnostics.phase] || diagnostics.phase}
            size="small"
            data-testid="debug-phase-chip"
            sx={{
              height: 24, fontSize: 11, fontWeight: 700,
              bgcolor: PHASE_TINTS[diagnostics.phase] || 'rgba(255,255,255,0.12)',
              color: '#f2f6fb',
            }}
          />
        )}
        {/* Said out loud, because a paused replay and a finished one look identical otherwise. */}
        {atBreakpoint && (
          <Chip
            label="BREAKPOINT"
            size="small"
            data-testid="debug-breakpoint-chip"
            sx={{
              height: 24, fontSize: 11, fontWeight: 700,
              bgcolor: 'rgba(255,184,107,0.26)', color: '#ffc078',
            }}
          />
        )}
      </Box>

      {/* ── the open premise and its gates ───────────────────────────────────────────────── */}
      {hasOpenTrade && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
            <Typography sx={SECTION}>Stop in force</Typography>
            <Typography sx={{ ...MONO, fontSize: 16, fontWeight: 700 }}>
              {formatLevel(diagnostics.stopInForce)}
            </Typography>
          </Box>
          {gates.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', gap: 1, px: 0.75, pb: 0.4, opacity: 0.6 }}>
                <Typography sx={{ ...MONO, fontSize: 11, width: COL_GATE }}>gate</Typography>
                <Typography sx={{ ...MONO, fontSize: 11, width: COL_LEVEL, textAlign: 'right' }}>
                  level
                </Typography>
                <Typography sx={{ ...MONO, fontSize: 11, width: COL_DIST, textAlign: 'right' }}>
                  to close
                </Typography>
              </Box>
              {gates.map((g) => <GateRow key={g.gate} reading={g} />)}
            </>
          ) : (
            // Stated rather than left blank: an empty panel reads as "the gates had nothing to
            // say" when the truth is that this run is not using them at all.
            <Typography sx={{ ...DATA_SMALL, opacity: 0.8 }}>
              No ratcheting gates in this run — the exit strategy is not RATCHETING_GATES.
            </Typography>
          )}
        </Box>
      )}

      {/* ── stop moves that landed on this bar ───────────────────────────────────────────── */}
      {stopUpdates && stopUpdates.length > 0 && (
        <Box sx={{ mb: 1.5 }} data-testid="debug-stop-updates">
          <Typography sx={{ ...SECTION, mb: 0.5 }}>Stop moved</Typography>
          {stopUpdates.map((u, i) => (
            <Typography
              key={i}
              sx={{ ...MONO, ...DATA_SMALL, color: '#8ff09b', py: 0.2, wordBreak: 'break-word' }}
            >
              {formatLevel(u.previousStop)} → {formatLevel(u.newStop)} · {u.reason}
            </Typography>
          ))}
        </Box>
      )}

      {/* ── the entry rules ──────────────────────────────────────────────────────────────── */}
      <Typography sx={{ ...SECTION, mb: 0.5 }}>Entry rules</Typography>
      {rules.length > 0
        ? rules.map((r) => <RuleRow key={r.rule} row={r} />)
        : (
          <Typography sx={{ ...DATA_SMALL, opacity: 0.8 }}>
            Not evaluated on this bar.
          </Typography>
        )}
      {/* The verdict, deliberately the largest text in the section: it is the one line that says
          what the FSM concluded, and the four rows above it are the working. */}
      <Typography
        sx={{
          ...DATA, mt: 1, pt: 1, fontWeight: 600,
          borderTop: '1px solid rgba(255,255,255,0.10)',
          color: 'rgba(242,246,251,0.95)',
        }}
      >
        {entryVerdict(diagnostics, hasOpenTrade)}
      </Typography>
    </Box>
  );
}

DynaManDebugPanel.propTypes = {
  /** The timeline frame at the playhead, carrying its `diagnostics` block. */
  frame: PropTypes.object,
  /** Stop moves announced on this frame, if any. */
  stopUpdates: PropTypes.array,
  /** True when the replay auto-paused here because a premise was committed. */
  atBreakpoint: PropTypes.bool,
};
